import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Check, Copy, Loader2, PartyPopper, CalendarClock, Clock, LayoutGrid, Smartphone, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface StepCompleteProps {
  establishmentSlug: string;
  zonesCount: number;
  tablesCount: number;
  timeSlotsCount: number;
  notificationsEnabled: boolean;
  bookingMode?: 'slot' | 'time';
  bookingIntervalMinutes?: number;
  reservationDurationMinutes?: number;
  onFinish: () => void;
  onGoToFloorPlan: () => void;
  onBack: () => void;
  saving: boolean;
}

export function StepComplete({
  establishmentSlug,
  zonesCount,
  tablesCount,
  timeSlotsCount,
  notificationsEnabled,
  bookingMode,
  bookingIntervalMinutes,
  reservationDurationMinutes,
  onFinish,
  onGoToFloorPlan,
  onBack,
  saving,
}: StepCompleteProps) {
  const [copied, setCopied] = useState(false);
  
  const bookingUrl = `https://piktable.ch/${establishmentSlug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      toast({
        title: "Lien copié !",
        description: "Le lien de réservation a été copié dans le presse-papier.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de copier le lien.",
        variant: "destructive",
      });
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}`;
  };

  const getBookingModeLabel = () => {
    if (!bookingMode) return null;
    if (bookingMode === 'slot') {
      return 'Mode créneaux';
    }
    return `Mode horaires libres (toutes les ${bookingIntervalMinutes} min, durée ${formatDuration(reservationDurationMinutes || 90)})`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <PartyPopper className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-semibold">Votre établissement est prêt !</h2>
        <p className="text-muted-foreground mt-2">
          Félicitations, vous pouvez maintenant recevoir des réservations
        </p>
      </div>

      {/* Summary */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          Récapitulatif
        </h3>
        <ul className="space-y-2">
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-600 shrink-0" />
            <span>
              {zonesCount > 0 ? `${zonesCount} zone${zonesCount > 1 ? 's' : ''} créée${zonesCount > 1 ? 's' : ''}` : 'Aucune zone configurée'}
            </span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-600 shrink-0" />
            <span>
              {tablesCount > 0 ? `${tablesCount} table${tablesCount > 1 ? 's' : ''} créée${tablesCount > 1 ? 's' : ''}` : 'Aucune table configurée'}
            </span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-600 shrink-0" />
            <span>
              {timeSlotsCount > 0 ? `${timeSlotsCount} service${timeSlotsCount > 1 ? 's' : ''} configuré${timeSlotsCount > 1 ? 's' : ''}` : 'Aucun créneau configuré'}
            </span>
          </li>
          {bookingMode && (
            <li className="flex items-center gap-2 text-sm">
              {bookingMode === 'time' ? (
                <CalendarClock className="w-4 h-4 text-green-600 shrink-0" />
              ) : (
                <Clock className="w-4 h-4 text-green-600 shrink-0" />
              )}
              <span>{getBookingModeLabel()}</span>
            </li>
          )}
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-600 shrink-0" />
            <span>
              {notificationsEnabled ? 'Notifications activées' : 'Notifications désactivées'}
            </span>
          </li>
        </ul>
      </div>

      {/* Floor plan CTA - Enhanced version */}
      {(zonesCount > 0 || tablesCount > 0) && (
        <div className="bg-gradient-to-r from-blue-50 to-primary/5 dark:from-blue-900/20 dark:to-primary/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎯</span>
            <h4 className="font-medium text-sm">Prochaine étape : Votre plan de salle</h4>
          </div>
          <ul className="space-y-1 text-sm text-muted-foreground ml-7">
            <li>• Positionnez vos tables sur le plan interactif</li>
            <li className="flex items-center gap-1">
              • Ou demandez une <span className="font-medium text-primary">configuration assistée gratuite</span>
            </li>
          </ul>
          <p className="text-xs text-muted-foreground ml-7 italic">
            Envoyez-nous une photo de votre salle, notre équipe s'occupe du reste !
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={onGoToFloorPlan}
            disabled={saving}
            className="ml-7 mt-2"
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            Configurer le plan de salle
          </Button>
        </div>
      )}

      {/* Booking link */}
      <div className="space-y-3">
        <h3 className="font-medium">📎 Votre lien de réservation</h3>
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border">
          <span className="flex-1 text-sm font-mono truncate">
            {bookingUrl}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="shrink-0"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Copié
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copier
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Partagez ce lien avec vos clients ! Vous pouvez aussi intégrer un widget sur votre site web depuis les paramètres.
        </p>
      </div>

      {/* Section test réservation */}
      <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h4 className="font-medium text-sm text-purple-800 dark:text-purple-200">
            Testez l'expérience de vos clients
          </h4>
        </div>
        <p className="text-sm text-purple-700 dark:text-purple-300 ml-7">
          Faites une réservation test pour voir exactement ce que vos clients verront, y compris l'email de confirmation automatique.
        </p>
        
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg ml-7">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-700 dark:text-amber-300">
            <p>
              <strong>Important :</strong> Comme vous êtes connecté, le lien vous redirigera vers votre dashboard.
            </p>
            <p className="mt-1">
              Pour tester comme un client :
            </p>
            <ul className="mt-1 space-y-0.5 ml-2">
              <li>• Ouvrez une <strong>fenêtre de navigation privée</strong> (Ctrl+Shift+N / Cmd+Shift+N)</li>
              <li>• Ou utilisez un <strong>autre appareil</strong> (téléphone, tablette)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onBack} disabled={saving}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button onClick={onFinish} disabled={saving} className="flex-1">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Configuration en cours...
            </>
          ) : (
            <>
              Accéder au tableau de bord
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
